import { useTheme } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

// Components
import { LoadingState, ErrorState } from 'components/ui/states';
import { ActionButton } from 'components/ui/buttons';

// Utility
import { formatPrice } from 'utility/numbers';

export default function Seats({ status, categories, products, onProductEdit }) {
    const theme = useTheme();

    if (status === 'loading') {
        return (
            <Paper sx={{ p: [2, 4], mb: 8 }}>
                <LoadingState />
            </Paper>
        );
    }

    if (status === 'error') {
        return (
            <Paper sx={{ p: [2, 4], mb: 8 }}>
                <ErrorState message="Unable to find tickets" />
            </Paper>
        );
    }

    return (
        <Box mb={8}>
            <Typography variant="h6" fontWeight="bold">
                Seating Categories (from your seating chart)
            </Typography>

            <Typography>
                Buyers choose a seat first, then select a ticket price. You can add multiple prices
                per seating category, like Adult and Child.
            </Typography>
            <Typography mb={2}>
                Create ticket prices and assign them to one or more seating categories. Once added,
                ticket prices will appear in the seating categories below. Seating categories are
                managed by the FrontDoor+ team.
            </Typography>

            {products.some((product) => product.seat_category_id == null) && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6">Prices needing seating categories</Typography>
                    <Typography>
                        These prices are preserved but cannot be sold until you assign a seating
                        category.
                    </Typography>
                    {products
                        .filter((product) => product.seat_category_id == null)
                        .map((product) => (
                            <Box
                                key={product.product_id}
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mt={2}
                            >
                                <Typography sx={{ flex: 1 }}>{product.prod_name}</Typography>
                                <Typography>{formatPrice(product.price)}</Typography>
                                <ActionButton onClick={() => onProductEdit(product.product_id)}>
                                    Edit
                                </ActionButton>
                            </Box>
                        ))}
                </Paper>
            )}

            {categories.map((category) => {
                const categoryProducts = products.filter(
                    (product) => product.seat_category_id === category.key,
                );

                return (
                    <Box key={category.key} mb={2}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: category.color || '#FFFFFF' }}>
                                    <TableRow>
                                        <TableCell>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                color={theme.palette.getContrastText(
                                                    category.color,
                                                )}
                                            >
                                                {categories.length > 1 ? category.name : 'Name'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="h6"
                                                color={theme.palette.getContrastText(
                                                    category.color,
                                                )}
                                            >
                                                Price
                                            </Typography>
                                        </TableCell>
                                        <TableCell width={110}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categoryProducts.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography>No ticket prices assigned.</Typography>
                                                <Typography>
                                                    Add a ticket price to make seats in this
                                                    category available for sale.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {categoryProducts.map((product) => (
                                        <TableRow key={product.product_id}>
                                            <TableCell>
                                                <Typography fontWeight="bold">
                                                    {product.prod_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatPrice(product.price)}
                                            </TableCell>
                                            <TableCell align="right" width={110}>
                                                <ActionButton
                                                    style={{ marginLeft: 8 }}
                                                    onClick={() =>
                                                        onProductEdit(product.product_id)
                                                    }
                                                >
                                                    Edit
                                                </ActionButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );
            })}
        </Box>
    );
}
